import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { posApi } from '@/services/api';
import { Plus, Edit2, Loader2 } from 'lucide-react';
import CategoryIcon from '@/components/pos/CategoryIcon';
import CategoryFormModal from '@/components/pos/CategoryFormModal';
import { getDefaultCategoryColor } from '@/constants/posCategoryIcons';
import type { POSCategory } from '@/types';

export default function CategoriesTab() {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState<POSCategory | null>(null);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categoriesList'],
    queryFn: async () => {
      const res = await posApi.getCategories();
      return res.data.message as POSCategory[];
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('categories.title')}</h3>
          <p className="text-sm text-gray-500">{t('categories.subtitle')}</p>
        </div>
        <button
          onClick={() => {
            setEditing(null);
            setIsModalOpen(true);
          }}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          {t('categories.add')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50 text-gray-900 font-medium border-b border-gray-200">
              <tr>
                <th className="px-6 py-4">{t('categories.name')}</th>
                <th className="px-6 py-4">{t('categories.type')}</th>
                <th className="px-6 py-4">{t('categories.displayOrder')}</th>
                <th className="px-6 py-4">{t('categories.icon')}</th>
                <th className="px-6 py-4 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categories.map((cat) => {
                const color = cat.pos_category_color || getDefaultCategoryColor(cat.category_name);
                return (
                  <tr key={cat.name} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <CategoryIcon
                          icon={cat.pos_category_icon}
                          image={cat.pos_category_image}
                          color={color}
                          size="sm"
                        />
                        <span className="font-medium text-gray-900">{cat.category_name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${cat.category_type === 'Service' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {cat.category_type || 'Product'}
                      </span>
                    </td>
                    <td className="px-6 py-4">{cat.display_order}</td>
                    <td className="px-6 py-4">
                      {cat.pos_category_icon || cat.pos_category_image ? (
                        <span className="text-xs text-gray-500">{cat.pos_category_icon || t('categories.image')}</span>
                      ) : (
                        <span className="text-xs text-gray-400">{t('common.none')}</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end">
                        <button
                          onClick={() => {
                            setEditing(cat);
                            setIsModalOpen(true);
                          }}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title={t('categories.edit')}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {categories.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-500">
                    {t('categories.empty')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isModalOpen && (
        <CategoryFormModal
          editing={editing}
          onClose={() => { setIsModalOpen(false); setEditing(null); }}
        />
      )}
    </div>
  );
}
